import { useState, useCallback, useRef } from 'react';
import {
  Crop as CropIcon,
  Download,
  Loader2,
  ImagePlus,
  Sparkles,
  Trash2,
  Upload,
  Wrench,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { PageContainer } from '../../../common/components/PageContainer';
import { useDropzone } from 'react-dropzone';
import ReactCrop from 'react-image-crop';
import type { Crop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';

// Type definitions
interface CroppedImageResult {
  url: string;
  blob: Blob;
  fileName: string;
}

// Card section title — repeated on every panel of this page
const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <h3 className="mb-3 text-sm font-medium text-foreground">{children}</h3>
);

// Grey inset section with an optional label
const InsetSection = ({ title, children, className }: { title?: string; children: React.ReactNode; className?: string }) => (
  <div className={cn('mb-5 border bg-muted/40 p-4', className)}>
    {title && (
      <p className="mb-3 text-xs font-medium text-muted-foreground">{title}</p>
    )}
    {children}
  </div>
);

// Styled DropZone content (the parent applies getRootProps/getInputProps)
const DropZoneArea = ({ isDragActive, isDragAccept, isDragReject }: {
  isDragActive: boolean;
  isDragAccept: boolean;
  isDragReject: boolean;
}) => {
  const { t } = useTranslation();

  return (
    <div
      className={cn(
        'flex min-h-[180px] cursor-pointer flex-col items-center justify-center border-2 border-dashed p-6 text-center transition-colors',
        isDragActive
          ? isDragAccept
            ? 'border-success bg-success/5'
            : 'border-destructive bg-destructive/5'
          : 'border-border bg-muted/40 hover:border-foreground/40 hover:bg-muted'
      )}
    >
      <Upload className="mb-4 size-10 text-muted-foreground" />
      <p className="mb-1 text-sm font-medium text-foreground">
        {isDragActive
          ? isDragAccept
            ? t('PAGES.IMAGE_CROPPER.UPLOAD_DROP_ACTIVE')
            : t('PAGES.IMAGE_CROPPER.UPLOAD_DROP_REJECT')
          : t('PAGES.IMAGE_CROPPER.UPLOAD_HINT')}
      </p>
      <p className="mb-1 text-xs text-muted-foreground">
        {t('PAGES.IMAGE_CROPPER.UPLOAD_HINT_CLICK')}
      </p>
      <p className="text-xs text-muted-foreground/70">
        {t('PAGES.IMAGE_CROPPER.UPLOAD_SUPPORT')}
      </p>
    </div>
  );
};

// Auto-cropper function inspired by the Python script
const autoCropWhiteBackground = (imageElement: HTMLImageElement, threshold = 245): Promise<Crop | null> => {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      resolve(null);
      return;
    }

    canvas.width = imageElement.naturalWidth;
    canvas.height = imageElement.naturalHeight;
    ctx.drawImage(imageElement, 0, 0);

    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;

    let minX = canvas.width, minY = canvas.height, maxX = 0, maxY = 0;
    let foundNonWhite = false;

    // Find bounding box of non-white pixels
    for (let y = 0; y < canvas.height; y++) {
      for (let x = 0; x < canvas.width; x++) {
        const idx = (y * canvas.width + x) * 4;
        const r = data[idx];
        const g = data[idx + 1];
        const b = data[idx + 2];

        // Convert to grayscale and check if below threshold
        const gray = 0.299 * r + 0.587 * g + 0.114 * b;

        if (gray < threshold) {
          foundNonWhite = true;
          minX = Math.min(minX, x);
          minY = Math.min(minY, y);
          maxX = Math.max(maxX, x);
          maxY = Math.max(maxY, y);
        }
      }
    }

    if (!foundNonWhite) {
      // If no non-white pixels found, return null
      resolve(null);
      return;
    }

    // Calculate crop area as percentage
    const cropArea: Crop = {
      unit: '%',
      x: (minX / canvas.width) * 100,
      y: (minY / canvas.height) * 100,
      width: ((maxX - minX + 1) / canvas.width) * 100,
      height: ((maxY - minY + 1) / canvas.height) * 100,
    };

    resolve(cropArea);
  });
};

// Function to create cropped image
const getCroppedImg = (image: HTMLImageElement, crop: { x: number; y: number; width: number; height: number }, fileName = 'cropped-image.png'): Promise<CroppedImageResult> => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    return Promise.reject(new Error('Canvas context not available'));
  }

  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;

  canvas.width = crop.width * scaleX;
  canvas.height = crop.height * scaleY;

  ctx.drawImage(
    image,
    crop.x * scaleX,
    crop.y * scaleY,
    crop.width * scaleX,
    crop.height * scaleY,
    0,
    0,
    canvas.width,
    canvas.height,
  );

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('Canvas is empty'));
        return;
      }
      const url = URL.createObjectURL(blob);
      resolve({ url, blob, fileName });
    }, 'image/png');
  });
};

export const ImageCropper = () => {
  const { t } = useTranslation();
  const [uploadedImage, setUploadedImage] = useState<File | null>(null);
  const [imageUrl, setImageUrl] = useState('');
  const [crop, setCrop] = useState<Crop>({
    unit: '%',
    width: 50,
    height: 50,
    x: 25,
    y: 25,
  });
  const [completedCrop, setCompletedCrop] = useState<Crop | null>(null);
  const [croppedImageUrl, setCroppedImageUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [autoThreshold, setAutoThreshold] = useState(245);
  const [previewSize, setPreviewSize] = useState(100);
  const [showOriginal, setShowOriginal] = useState(true);

  const imageRef = useRef<HTMLImageElement>(null);

  // Handle file drop
  const onDrop = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (file) {
      setUploadedImage(file);
      const url = URL.createObjectURL(file);
      setImageUrl(url);
      setCroppedImageUrl('');
      // Reset crop area
      setCrop({
        unit: '%',
        width: 50,
        height: 50,
        x: 25,
        y: 25,
      });
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject } = useDropzone({
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.bmp', '.webp']
    },
    onDrop,
    multiple: false
  });

  // Auto-crop white background
  const handleAutoCrop = async () => {
    if (!imageRef.current) return;

    setLoading(true);
    try {
      const autoCropArea = await autoCropWhiteBackground(imageRef.current, autoThreshold);
      if (autoCropArea) {
        setCrop(autoCropArea);
      } else {
        alert(t('PAGES.IMAGE_CROPPER.NO_NON_WHITE_CONTENT'));
      }
    } catch (error) {
      console.error('Auto-crop failed:', error);
      alert(t('PAGES.IMAGE_CROPPER.AUTO_CROP_FAILED'));
    }
    setLoading(false);
  };

  // Generate cropped image
  const handleCropImage = async () => {
    if (!imageRef.current || !crop.width || !crop.height) return;

    setLoading(true);
    try {
      const croppedImage = await getCroppedImg(
        imageRef.current,
        {
          x: (crop.x / 100) * imageRef.current.width,
          y: (crop.y / 100) * imageRef.current.height,
          width: (crop.width / 100) * imageRef.current.width,
          height: (crop.height / 100) * imageRef.current.height,
        },
        uploadedImage?.name || 'cropped-image.png'
      );
      setCroppedImageUrl(croppedImage.url);
    } catch (error) {
      console.error('Crop failed:', error);
      alert(t('PAGES.IMAGE_CROPPER.CROP_FAILED'));
    }
    setLoading(false);
  };

  // Download cropped image
  const handleDownload = () => {
    if (!croppedImageUrl) return;

    const link = document.createElement('a');
    link.href = croppedImageUrl;
    link.download = `cropped-${uploadedImage?.name || 'image.png'}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Reset everything
  const handleReset = () => {
    setUploadedImage(null);
    setImageUrl('');
    setCroppedImageUrl('');
    setCrop({
      unit: '%',
      width: 50,
      height: 50,
      x: 25,
      y: 25,
    });
    setCompletedCrop(null);
  };

  // Clear cropped result
  const handleClearCrop = () => {
    setCroppedImageUrl('');
  };

  return (
    <PageContainer breadcrumbs={[
      { label: t('SIDEBAR.VISUALIZER.TITLE') },
      { label: t('SIDEBAR.VISUALIZER.TOOLS'), icon: <Wrench className="size-5" /> },
      { label: t('SIDEBAR.VISUALIZER.IMAGE_CROPPER') }
    ]}>
      <div>
        <h1 className="font-heading mb-8 text-xl font-medium tracking-tight text-foreground">
          {t('PAGES.IMAGE_CROPPER.TITLE')}
        </h1>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {/* Left Panel - Upload and Controls */}
          <div className="flex flex-col gap-6 md:col-span-1">
            {/* Upload Area */}
            <Card>
              <CardContent>
                <SectionTitle>{t('PAGES.IMAGE_CROPPER.UPLOAD_IMAGE')}</SectionTitle>

                <div {...getRootProps()}>
                  <input {...getInputProps()} />
                  <DropZoneArea
                    isDragActive={isDragActive}
                    isDragAccept={isDragAccept}
                    isDragReject={isDragReject}
                  />
                </div>

                {uploadedImage && (
                  <div className="mt-4 border bg-muted/40 p-4">
                    <p className="text-xs font-medium text-muted-foreground">
                      {t('PAGES.IMAGE_CROPPER.UPLOADED_FILE')}: {uploadedImage.name}
                    </p>
                    <p className="text-xs text-muted-foreground/70">
                      {t('PAGES.IMAGE_CROPPER.FILE_SIZE')}: {(uploadedImage.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Auto-Crop Controls */}
            {uploadedImage && (
              <Card>
                <CardContent>
                  <SectionTitle>{t('PAGES.IMAGE_CROPPER.AUTO_CROP_SETTINGS')}</SectionTitle>

                  <InsetSection>
                    <p className="mb-2 text-xs font-medium text-foreground">
                      {t('PAGES.IMAGE_CROPPER.WHITE_THRESHOLD')}: {autoThreshold}
                    </p>
                    <Slider
                      value={[autoThreshold]}
                      onValueChange={(value) => setAutoThreshold(value[0])}
                      min={200}
                      max={255}
                      step={1}
                      className="mt-2"
                    />
                    <div className="mt-1.5 mb-2 flex justify-between text-xs text-muted-foreground">
                      <span>200</span>
                      <span>255</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {t('PAGES.IMAGE_CROPPER.WHITE_THRESHOLD_HELP')}
                    </p>
                  </InsetSection>

                  <Button
                    variant="outline"
                    onClick={handleAutoCrop}
                    disabled={loading}
                    className="mb-2 w-full"
                  >
                    {loading
                      ? <Loader2 data-icon="inline-start" className="animate-spin" />
                      : <Sparkles data-icon="inline-start" />}
                    {t('PAGES.IMAGE_CROPPER.AUTO_CROP_BUTTON')}
                  </Button>

                  <p className="text-center text-xs text-muted-foreground">
                    {t('PAGES.IMAGE_CROPPER.AUTO_CROP_DESCRIPTION')}
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Manual Crop Controls */}
            {uploadedImage && (
              <Card>
                <CardContent>
                  <SectionTitle>{t('PAGES.IMAGE_CROPPER.MANUAL_CROP_CONTROLS')}</SectionTitle>

                  <div className="flex flex-col gap-3">
                    <Button
                      variant="outline"
                      onClick={handleCropImage}
                      disabled={loading || !crop.width || !crop.height}
                      className="w-full"
                    >
                      {loading
                        ? <Loader2 data-icon="inline-start" className="animate-spin" />
                        : <CropIcon data-icon="inline-start" />}
                      {t('PAGES.IMAGE_CROPPER.APPLY_CROP')}
                    </Button>

                    {croppedImageUrl && (
                      <>
                        <Button variant="outline" onClick={handleDownload} className="w-full">
                          <Download data-icon="inline-start" />
                          {t('PAGES.IMAGE_CROPPER.DOWNLOAD_CROPPED')}
                        </Button>

                        <Button
                          variant="outline"
                          onClick={handleClearCrop}
                          className="w-full border-destructive/40 text-destructive hover:bg-destructive/5 hover:text-destructive"
                        >
                          <Trash2 data-icon="inline-start" />
                          {t('PAGES.IMAGE_CROPPER.CLEAR_CROP_RESULT')}
                        </Button>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Preview Controls */}
            {uploadedImage && (
              <Card>
                <CardContent>
                  <SectionTitle>{t('PAGES.IMAGE_CROPPER.PREVIEW_CONTROLS')}</SectionTitle>

                  <InsetSection>
                    <p className="mb-2 text-xs font-medium text-foreground">
                      {t('PAGES.IMAGE_CROPPER.PREVIEW_SIZE')}: {previewSize}%
                    </p>
                    <Slider
                      value={[previewSize]}
                      onValueChange={(value) => setPreviewSize(value[0])}
                      min={25}
                      max={200}
                      step={25}
                      className="mt-2"
                    />
                    <div className="mt-1.5 flex justify-between text-xs text-muted-foreground">
                      <span>25%</span>
                      <span>200%</span>
                    </div>
                  </InsetSection>

                  <Label className="flex items-center gap-2 font-normal">
                    <Switch
                      checked={showOriginal}
                      onCheckedChange={(checked) => setShowOriginal(checked)}
                    />
                    <span className="text-sm text-foreground">
                      {t('PAGES.IMAGE_CROPPER.SHOW_ORIGINAL')}
                    </span>
                  </Label>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right Panel - Image Preview and Cropping */}
          <div className="md:col-span-2">
            {!uploadedImage ? (
              <div className="flex h-[60vh] flex-col items-center justify-center border bg-muted/40 p-12 text-center">
                <ImagePlus className="mb-6 size-16 text-muted-foreground" />
                <h2 className="font-heading mb-3 text-xl font-medium tracking-tight text-foreground">
                  {t('PAGES.IMAGE_CROPPER.NO_IMAGE_SELECTED')}
                </h2>
                <p className="max-w-[500px] text-sm text-muted-foreground">
                  {t('PAGES.IMAGE_CROPPER.NO_IMAGE_DESCRIPTION')}
                </p>
              </div>
            ) : (
              <div className="flex flex-col gap-6">
                {/* Original Image with Crop Tool */}
                <Card>
                  <CardContent>
                    <SectionTitle>{t('PAGES.IMAGE_CROPPER.ORIGINAL_IMAGE')}</SectionTitle>

                    {/* The wrapper's overflow and ReactCrop's transform are part of
                        the crop coordinate space — left exactly as they were. */}
                    <div className="max-h-[60vh] overflow-auto border bg-muted/40 p-1">
                      <ReactCrop
                        crop={crop}
                        onChange={(newCrop) => setCrop(newCrop)}
                        onComplete={(c) => setCompletedCrop(c)}
                        aspect={undefined}
                        style={{
                          transform: `scale(${previewSize / 100})`,
                          transformOrigin: 'top left'
                        }}
                      >
                        <img
                          ref={imageRef}
                          src={imageUrl}
                          alt="Crop"
                          style={{
                            maxWidth: '100%',
                            maxHeight: '100%'
                          }}
                        />
                      </ReactCrop>
                    </div>

                    <p className="mt-2 block text-xs text-muted-foreground">
                      {t('PAGES.IMAGE_CROPPER.CROP_INSTRUCTION')}
                    </p>
                  </CardContent>
                </Card>

                {/* Cropped Result */}
                {croppedImageUrl && (
                  <Card className="border-foreground">
                    <CardContent>
                      <div className="mb-4 border bg-muted/40 p-4">
                        <p className="text-sm font-medium text-foreground">
                          {t('PAGES.IMAGE_CROPPER.CROPPED_RESULT')}
                        </p>
                      </div>

                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        {showOriginal && (
                          <InsetSection title={t('PAGES.IMAGE_CROPPER.ORIGINAL')} className="mb-0">
                            <img
                              src={imageUrl}
                              alt="Original"
                              className="max-h-[300px] max-w-full border object-contain"
                            />
                          </InsetSection>
                        )}

                        <InsetSection
                          title={t('PAGES.IMAGE_CROPPER.CROPPED')}
                          className={cn('mb-0', !showOriginal && 'md:col-span-2')}
                        >
                          <img
                            src={croppedImageUrl}
                            alt="Cropped"
                            className="max-h-[300px] max-w-full border object-contain"
                          />
                        </InsetSection>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </PageContainer>
  );
};
